<template>
  <div class="moment-block">
    <div class="moment-header">
      <span class="moment-nick">{{ moment.nickname }}</span>
      <span class="moment-time">{{ formatTime(moment.createdAt) }}</span>
    </div>
    <div class="moment-main">
      <div class="moment-content">{{ moment.content }}</div>
      <!-- 🎨 微信朋友圈级智能媒体布局 -->
      <div v-if="totalMediaCount > 0" :class="mediaGridClass">
        <!-- 显示图片 -->
        <div
          v-for="(img, idx) in moment.images || []"
          :key="`img-${idx}`"
          class="moment-media-wrap"
          @click="previewImages(moment.images, idx)"
        >
          <img :src="img" class="moment-media-item" />
        </div>
        <!-- 🎬 微信级智能视频 -->
        <div
          v-for="(video, idx) in moment.videos || []"
          :key="`video-${idx}`"
          class="moment-media-wrap moment-video-wrap"
          @click="handleVideoClick(video, idx)"
        >
          <video
            :ref="`video-${moment.id}-${idx}`"
            :src="video"
            class="moment-media-item moment-video"
            muted
            playsinline
            preload="metadata"
            :poster="getVideoPoster(video)"
            @loadedmetadata="onVideoLoaded"
            @canplay="onVideoCanPlay"
            @play="onVideoPlay"
            @pause="onVideoPause"
            @ended="onVideoEnded"
          />
          <!-- 🎮 自定义播放控制层 -->
          <div class="video-overlay" v-show="!isVideoPlaying(`${moment.id}-${idx}`)">
            <div class="video-play-btn">
              <svg class="play-icon" viewBox="0 0 24 24" width="48" height="48">
                <path fill="white" d="M8 5v14l11-7z"/>
              </svg>
            </div>
            <div class="video-duration" v-if="getVideoDuration(`${moment.id}-${idx}`)">
              {{ formatDuration(getVideoDuration(`${moment.id}-${idx}`)) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="moment.comments && moment.comments.length" class="moment-comments">
      <div v-for="(c, i) in moment.comments" :key="i" class="moment-comment-bubble">
        <span class="comment-nick">{{ c.nickname }}：</span>{{ c.content }}
      </div>
    </div>
    <div v-if="showCommentInput" class="moment-comment-input-wrap">
      <van-field
        v-model="commentContent"
        placeholder="写下你的评论..."
        class="moment-comment-input"
        @keyup.enter="handleComment"
      />
      <van-button
        size="small"
        type="primary"
        round
        class="moment-comment-btn"
        @click="handleComment"
      >评论</van-button>
    </div>
    <div class="moment-footer">
      <span class="moment-action-left">
        <span class="moment-action" @click="onLike" :class="{ active: liked }">
          <!-- 这里可以改点赞的颜色 -->
          <van-icon :name="liked ? 'like' : 'like-o'" color="#7f7f7f" size="16" />
          <span v-if="moment.likeUsers && moment.likeUsers.length" class="like-nick-list">
            {{ moment.likeUsers.map(u => u.nickname).join('、') }}
          </span>
        </span>
      </span>
      <span class="moment-action-right">
        <span class="moment-action" @click="toggleCommentInput">
          <van-icon name="comment-o" color="#7f7f7f" size="18" />
        </span>
      </span>
    </div>
  </div>
</template>

<script>
import { ImagePreview, Toast } from 'vant'
import { likeOrchardMoment, unlikeOrchardMoment, commentMoment } from '@/api/orchard'
export default {
  name: 'OrchardMoment',
  props: {
    moment: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      showCommentInput: false,
      commentContent: '',
      // 🎬 视频播放状态管理
      playingVideos: {}, // 记录正在播放的视频
      videoDurations: {}, // 记录视频时长
      videoObserver: null // Intersection Observer实例
    }
  },
  computed: {
    liked () {
      const userId = localStorage.getItem('userId')
      return Array.isArray(this.moment.likeUsers) && this.moment.likeUsers.some(u => String(u.userId) === String(userId))
    },
    // 🎯 计算媒体总数量
    totalMediaCount () {
      const imageCount = (this.moment.images && this.moment.images.length) || 0
      const videoCount = (this.moment.videos && this.moment.videos.length) || 0
      return imageCount + videoCount
    },
    // 🎨 根据媒体数量生成对应的CSS类名（微信朋友圈规则）
    mediaGridClass () {
      const count = this.totalMediaCount
      return `moment-media-grid moment-media-${count}`
    },
    // 🎬 是否是第一个moment（用于自动播放判断）
    isFirstMoment () {
      // 需要父组件传入index属性
      return this.$attrs.momentIndex === 0
    }
  },
  mounted () {
    // 🎬 初始化视频功能
    this.setupVideoObserver()

    // 🎯 监听全局暂停视频事件
    this.$root.$on('pause-all-videos', (excludeMomentId) => {
      if (excludeMomentId !== this.moment.id) {
        this.pauseAllVideos(true) // 🚨 传入true，标记来自全局事件
      }
    })
  },
  beforeDestroy () {
    // 🧹 清理视频观察器
    if (this.videoObserver) {
      this.videoObserver.disconnect()
      this.videoObserver = null
    }

    // 🧹 清理事件监听
    this.$root.$off('pause-all-videos')

    // 🧹 暂停所有视频
    this.pauseAllVideos(true)
  },
  methods: {
    formatTime (t) {
      return t ? t.replace('T', ' ').slice(11, 16) : ''
    },
    previewImages (images, idx) {
      ImagePreview({ images, startPosition: idx })
    },
    async onLike () {
      if (this.liked) {
        try {
          await unlikeOrchardMoment(String(this.moment.id))
          this.$emit('update-like', { moment: this.moment, liked: false })
          Toast('已取消点赞')
        } catch (e) {
          Toast('取消点赞失败，请稍后重试')
        }
      } else {
        try {
          await likeOrchardMoment(String(this.moment.id))
          this.$emit('update-like', { moment: this.moment, liked: true })
          Toast('点赞成功！')
        } catch (e) {
          Toast('点赞失败，请稍后重试')
        }
      }
    },
    toggleCommentInput () {
      this.showCommentInput = !this.showCommentInput
      if (this.showCommentInput) {
        this.commentContent = ''
      }
    },
    async handleComment () {
      if (!this.commentContent.trim()) {
        Toast('请输入评论内容')
        return
      }
      try {
        await commentMoment(this.moment.id, this.commentContent.trim())
        // 通过事件通知父组件
        const comment = {
          userId: 'me',
          nickname: localStorage.getItem('nickname') || '我',
          content: this.commentContent.trim(),
          createdAt: new Date().toISOString()
        }
        this.$emit('comment', { moment: this.moment, comment })
        this.commentContent = ''
        this.showCommentInput = false
        Toast('评论成功！')
      } catch (e) {
        Toast('评论失败，请稍后重试')
      }
    },

    // 🎬 =================== 视频相关方法 ===================

    // 🎯 处理视频点击事件
    async handleVideoClick (videoUrl, index) {
      const videoId = `${this.moment.id}-${index}`
      const videoEl = this.$refs[`video-${videoId}`]?.[0]

      if (!videoEl) return

      try {
        const isCurrentlyPlaying = !videoEl.paused && !videoEl.ended

        if (isCurrentlyPlaying) {
          // 如果正在播放，暂停当前视频
          videoEl.pause()
          this.$set(this.playingVideos, videoId, false)
        } else {
          // 暂停其他所有视频（不触发全局事件）
          this.pauseAllVideos(false)

          // 播放当前视频
          videoEl.muted = false // 用户点击时取消静音
          await videoEl.play()
          this.$set(this.playingVideos, videoId, true)

          // 🎯 移动端进入全屏播放
          this.$nextTick(() => {
            if (this.isMobile()) {
              this.enterFullscreen(videoEl)
            }
          })
        }
      } catch (error) {
        console.warn('视频播放失败:', error)
        this.$set(this.playingVideos, videoId, false)
        Toast('视频播放失败，请稍后重试')
      }
    },

    // 🔇 暂停所有视频
    pauseAllVideos (fromGlobalEvent = false) {
      Object.keys(this.playingVideos).forEach(videoId => {
        const videoEl = this.$refs[`video-${videoId}`]?.[0]
        if (videoEl && !videoEl.paused) {
          videoEl.pause()
        }
        this.$set(this.playingVideos, videoId, false)
      })

      // 🚨 只有非全局事件触发时才发射事件，避免无限循环
      if (!fromGlobalEvent) {
        this.$root.$emit('pause-all-videos', this.moment.id)
      }
    },

    // 📊 检查视频是否正在播放
    isVideoPlaying (videoId) {
      const videoEl = this.$refs[`video-${videoId}`]?.[0]
      if (videoEl) {
        // 基于实际DOM状态判断
        return !videoEl.paused && !videoEl.ended
      }
      // 降级到状态管理
      return this.playingVideos[videoId] || false
    },

    // 📺 获取视频时长
    getVideoDuration (videoId) {
      return this.videoDurations[videoId] || 0
    },

    // ⏱️ 格式化时长
    formatDuration (seconds) {
      if (!seconds || seconds < 1) return ''
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },

    // 🖼️ 获取视频封面（简化版，实际项目中需要服务器端生成）
    getVideoPoster (videoUrl) {
      // 在真实项目中，这里应该返回视频缩略图的URL
      // 通常由后端在视频上传时自动生成第一帧作为封面
      return '' // 暂时返回空，让浏览器显示第一帧
    },

    // 📹 视频元数据加载完成
    onVideoLoaded (event) {
      const videoEl = event.target
      const videoId = this.getVideoIdFromElement(videoEl)
      if (videoId && videoEl.duration) {
        this.$set(this.videoDurations, videoId, videoEl.duration)
      }
    },

    // ✅ 视频可以播放
    onVideoCanPlay (event) {
      const videoEl = event.target
      const videoId = this.getVideoIdFromElement(videoEl)

      // 🎯 第一个moment的第一个视频自动播放（静音）
      if (this.isFirstMoment && videoId && videoId.endsWith('-0')) {
        this.tryAutoPlay(videoEl, videoId)
      }
    },

    // 🎮 尝试自动播放
    async tryAutoPlay (videoEl, videoId) {
      try {
        videoEl.muted = true // 确保静音
        await videoEl.play()
        this.$set(this.playingVideos, videoId, true)
      } catch (error) {
        // 自动播放失败（通常是用户还没有交互过）
        console.log('自动播放失败，需要用户交互:', error)
      }
    },

    // 🎵 视频开始播放事件
    onVideoPlay (event) {
      const videoEl = event.target
      const videoId = this.getVideoIdFromElement(videoEl)
      if (videoId) {
        this.$set(this.playingVideos, videoId, true)
      }
    },

    // ⏸️ 视频暂停事件
    onVideoPause (event) {
      const videoEl = event.target
      const videoId = this.getVideoIdFromElement(videoEl)
      if (videoId) {
        this.$set(this.playingVideos, videoId, false)
      }
    },

    // 🔚 视频播放结束事件
    onVideoEnded (event) {
      const videoEl = event.target
      const videoId = this.getVideoIdFromElement(videoEl)
      if (videoId) {
        this.$set(this.playingVideos, videoId, false)
      }
    },

    // 🔧 从video元素获取videoId
    getVideoIdFromElement (videoEl) {
      const ref = Object.keys(this.$refs).find(key =>
        this.$refs[key] && this.$refs[key][0] === videoEl
      )
      return ref ? ref.replace('video-', '') : null
    },

    // 👀 设置滚动自动播放观察器
    setupVideoObserver () {
      if (!window.IntersectionObserver) return

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            const videoEl = entry.target
            const videoId = this.getVideoIdFromElement(videoEl)

            if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
              // 视频进入可视区域中心，尝试自动播放
              if (!this.isVideoPlaying(videoId)) {
                this.tryAutoPlay(videoEl, videoId)
              }
            } else {
              // 视频离开可视区域，暂停播放
              if (this.isVideoPlaying(videoId)) {
                videoEl.pause()
                this.$set(this.playingVideos, videoId, false)
              }
            }
          })
        },
        {
          threshold: 0.5, // 50%可见时触发
          rootMargin: '0px'
        }
      )

      // 观察所有视频元素
      this.$nextTick(() => {
        Object.keys(this.$refs).forEach(key => {
          if (key.includes('video-') && this.$refs[key]?.[0]) {
            this.videoObserver.observe(this.$refs[key][0])
          }
        })
      })
    },

    // 📱 检测是否为移动设备
    isMobile () {
      return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             window.innerWidth <= 768
    },

    // 🎯 进入全屏播放
    enterFullscreen (videoEl) {
      try {
        if (videoEl.requestFullscreen) {
          videoEl.requestFullscreen()
        } else if (videoEl.webkitRequestFullscreen) {
          videoEl.webkitRequestFullscreen()
        } else if (videoEl.webkitEnterFullscreen) {
          // iOS Safari
          videoEl.webkitEnterFullscreen()
        } else if (videoEl.msRequestFullscreen) {
          // IE/Edge
          videoEl.msRequestFullscreen()
        }
      } catch (error) {
        console.warn('全屏播放失败:', error)
        // 全屏失败不影响正常播放，不需要提示用户
      }
    }
  }
}
</script>

<style scoped>
.moment-block {
  position: relative;
  background: transparent;
  padding: 18px 24px 0px 0;
  border-radius: 0;
  min-height: 48px;
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 12px;
}

.moment-header {
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.moment-nick {
  font-size: 1rem;
  font-weight: 600;
  color: #667eea;
}

.moment-time {
  color: #a0aec0 !important;
  font-size: 0.85rem;
  font-weight: 400;
  letter-spacing: 0.5px;
}

.moment-main {
  margin-left: 16px;
}

.moment-content {
  font-size: 1.1rem;
  font-weight: 400;
  color: #34495e;
  margin-bottom: 14px;
  line-height: 1.75;
  word-break: break-all;
  letter-spacing: 0.2px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', '微软雅黑', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  text-shadow: 0 0.5px 1px rgba(0, 0, 0, 0.05);
  opacity: 0.95;
}

/* 🎨 微信朋友圈级智能媒体布局 - 基础样式 */
.moment-media-grid {
  display: grid;
  gap: 6px;
  margin-bottom: 12px;
  max-width: 100%;
  justify-content: start;
}

/* 📱 单张媒体 - 大图显示 */
.moment-media-1 {
  grid-template-columns: 1fr;
  max-width: 240px;
}

.moment-media-1 .moment-media-wrap {
  width: 100%;
  height: 200px;
  border-radius: 12px;
}

/* 📱 两张媒体 - 横向排列 */
.moment-media-2 {
  grid-template-columns: 1fr 1fr;
  max-width: 240px;
}

.moment-media-2 .moment-media-wrap {
  width: 100%;
  height: 116px;
  border-radius: 10px;
}

/* 📱 三张媒体 - 第一行1张，第二行2张 */
.moment-media-3 {
  grid-template-columns: 1fr 1fr;
  max-width: 240px;
}

.moment-media-3 .moment-media-wrap:first-child {
  grid-column: 1 / -1; /* 第一张占满整行 */
  width: 100%;
  height: 116px;
  border-radius: 10px;
}

.moment-media-3 .moment-media-wrap:not(:first-child) {
  width: 100%;
  height: 116px;
  border-radius: 10px;
}

/* 📱 四张媒体 - 2x2方格 */
.moment-media-4 {
  grid-template-columns: 1fr 1fr;
  max-width: 240px;
}

.moment-media-4 .moment-media-wrap {
  width: 100%;
  height: 116px;
  border-radius: 10px;
}

/* 📱 五张媒体 - 2列均匀 + 底部横铺，避免空格且更规整 */
.moment-media-5 {
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: 120px;
  max-width: 320px;
}

.moment-media-5 .moment-media-wrap:nth-child(5) {
  grid-column: 1 / -1; /* 第五张横铺一整行 */
  height: 130px;
  border-radius: 10px;
}

.moment-media-5 .moment-media-wrap {
  width: 100%;
  height: 120px;
  border-radius: 10px;
}

/* 📱 六张媒体 - 3x2网格 */
.moment-media-6 {
  grid-template-columns: 1fr 1fr 1fr;
  max-width: 270px;
}

.moment-media-6 .moment-media-wrap {
  width: 100%;
  height: 84px;
  border-radius: 8px;
}

/* 📱 七张媒体 - 第一行1张大图，后面3x2 */
.moment-media-7 {
  grid-template-columns: 1fr 1fr 1fr;
  max-width: 270px;
}

.moment-media-7 .moment-media-wrap:first-child {
  grid-column: 1 / -1;
  width: 100%;
  height: 120px;
  border-radius: 10px;
}

.moment-media-7 .moment-media-wrap:not(:first-child) {
  width: 100%;
  height: 84px;
  border-radius: 8px;
}

/* 📱 八张媒体 - 第一行2张，后面3x2 */
.moment-media-8 {
  grid-template-columns: 1fr 1fr 1fr;
  max-width: 270px;
}

.moment-media-8 .moment-media-wrap:nth-child(1),
.moment-media-8 .moment-media-wrap:nth-child(2) {
  grid-column: span 1.5;
  height: 100px;
  border-radius: 10px;
}

.moment-media-8 .moment-media-wrap:nth-child(n+3) {
  width: 100%;
  height: 84px;
  border-radius: 8px;
}

/* 📱 九张媒体 - 3x3九宫格（微信最大值） */
.moment-media-9 {
  grid-template-columns: 1fr 1fr 1fr;
  max-width: 270px;
}

.moment-media-9 .moment-media-wrap {
  width: 100%;
  height: 84px;
  border-radius: 8px;
}

/* 🎯 媒体项通用样式 */
.moment-media-wrap {
  overflow: hidden;
  background: #f7fafc;
  border: 1px solid rgba(160, 174, 192, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  /* 🚀 GPU加速优化 */
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
}

.moment-media-wrap:hover {
  border-color: rgba(102, 126, 234, 0.3);
  transform: scale(1.02);
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.15);
}

.moment-media-wrap:active {
  transform: scale(0.98);
}

.moment-media-item {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.25s ease;
}

/* 🎬 微信级视频特殊样式 */
.moment-video-wrap {
  position: relative;
  overflow: hidden;
}

.moment-video {
  border-radius: 0; /* 视频内部不需要圆角 */
  object-fit: cover;
  background: #000; /* 黑色背景，避免灰色 */
}

/* 🎮 自定义视频控制层 */
.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.15); /* 轻微半透明 */
  transition: opacity 0.3s ease;
  cursor: pointer;
}

.video-overlay:hover {
  background: rgba(0, 0, 0, 0.25);
}

/* 🎯 播放按钮 */
.video-play-btn {
  width: 60px;
  height: 60px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  border: 2px solid rgba(255, 255, 255, 0.8);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  /* 🚀 GPU加速 */
  transform: translateZ(0);
  will-change: transform;
}

.video-play-btn:hover {
  transform: scale(1.1);
  background: rgba(0, 0, 0, 0.75);
  border-color: rgba(255, 255, 255, 1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.video-play-btn:active {
  transform: scale(0.95);
}

/* ▶️ 播放图标 */
.play-icon {
  margin-left: 2px; /* 视觉居中调整 */
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

/* ⏱️ 视频时长标签 */
.video-duration {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
  backdrop-filter: blur(4px);
  font-family: -apple-system, BlinkMacSystemFont, 'SF Mono', Consolas, monospace;
  letter-spacing: 0.5px;
}

/* 📱 移动端适配 */
@media (max-width: 600px) {
  .video-play-btn {
    width: 50px;
    height: 50px;
  }

  .play-icon {
    width: 36px;
    height: 36px;
  }

  .video-duration {
    font-size: 0.7rem;
    padding: 1px 4px;
    bottom: 6px;
    right: 6px;
  }
}

/* 🎯 视频播放状态样式 */
.moment-video-wrap.playing .video-overlay {
  opacity: 0;
  pointer-events: none;
}

/* 🔇 隐藏原生视频控件 */
.moment-video::-webkit-media-controls {
  display: none !important;
}

.moment-video::-webkit-media-controls-enclosure {
  display: none !important;
}

/* 📱 iOS全屏播放优化 */
.moment-video::-webkit-media-controls-panel {
  background: linear-gradient(transparent, rgba(0,0,0,0.5));
}

.moment-video::-webkit-media-controls-play-button {
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
}

.moment-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  margin-bottom: 2px;
}

.moment-action-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.moment-action-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.moment-action {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: transform 0.15s;
  padding: 4px 8px;
  border-radius: 12px;
}

.moment-action:hover {
  background: rgba(102, 126, 234, 0.1);
}

.moment-action.active {
  transform: scale(1.1);
}

.moment-likes {
  color: #667eea;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 2px;
}

.moment-comments {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.moment-comment-bubble {
  background: transparent;
  color: #718096;
  border-radius: 0;
  padding: 2px 0;
  font-size: 0.85rem;
  display: block;
  line-height: 1.4;
  border: none;
}

.comment-nick {
  font-weight: 500;
  color: #9ca3af;
  margin-right: 4px;
}

.like-nick-list {
  color: #9ca3af;
  font-size: 0.85rem;
  font-weight: 500;
  margin-left: 4px;
  letter-spacing: 0.5px;
}

.moment-comment-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 4px 0;
  padding: 8px;
  background: rgba(247, 250, 252, 0.8);
  border-radius: 12px;
  backdrop-filter: blur(8px);
}

.moment-comment-input {
  flex: 1;
  min-height: 36px;
  height: 36px;
  line-height: 36px;
  padding: 0px 0px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  border: 1px solid rgba(160, 174, 192, 0.2);
}

::v-deep .moment-comment-input .van-field__control {
  color: #2d3748;
  background: transparent;
}

::v-deep .moment-comment-input:focus-within {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

.moment-comment-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transition: all 0.2s ease;
  min-width: 60px;
}

.moment-comment-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.moment-comment-btn:active {
  transform: translateY(0);
}
</style>
